import {
  Connection,
  CreateConnectionRequest,
  UpdateConnectionRequest
} from "../entities/connection";
import {
  CreateDirectoryRequest,
  Directory,
  UpdateDirectoryRequest
} from "../entities/directory";
import { Organization } from "../entities/organization";
import { PaginationOptions } from "../entities/pagination_options";
import {
  ResGetConnectionSessions,
  ResGetEnterpriseConnections,
  ResGetEnterpriseDirectories
} from "../types/api-types";
import { BaseOrganizationEndpoints } from "./base";
import { DirectoryLinks } from "./response/directory_links";
import { ResponseWithStatus } from "./response/response_with_status";

export class Enterprises extends BaseOrganizationEndpoints {
  async domains() {
    const response = await this.requestWithStatus<{
      enterprise_domains: string[];
    }>("GET", "/enterprise");
    return response;
  }

  /**
   * Updates enterprise domains for an organization.
   *
   * Routes to Core (Go) endpoint which handles provider routing internally
   * (WorkOS vs Ory based on organization's EnterpriseProvider setting).
   */
  async updateDomains(
    domains: string[],
    organizationId?: string,
    organizationName?: string
  ) {
    return await this.requestWithStatus<
      Organization | { error: "domains:duplicate" | "string" }
    >("POST", "/v1/directory/setup", {
      name: organizationName || "Organization",
      domains: domains
    });
  }

  /**
   * Generates and returns the provider setup link for a single directory.
   */
  async directoryLinks(
    directoryId: string
  ): Promise<ResponseWithStatus<DirectoryLinks>> {
    return await this.requestWithStatus<DirectoryLinks>(
      "GET",
      `/v1/directories/${directoryId}/links`
    );
  }

  /**
   * Lists all SSO connections for the organization.
   *
   * Routes to Core (Go) endpoint which returns connections from the database.
   */
  async connections(
    pagination: PaginationOptions = {
      cursor: null,
      limit: null
    }
  ) {
    const response = await this.requestWithStatus<ResGetEnterpriseConnections>(
      "GET",
      this.paginatedEndpointUrl("/v1/connections", pagination)
    );
    return response;
  }

  /**
   * Lists all directory sync configurations for the organization.
   *
   * Routes to Core (Go) endpoint which returns directories from the database.
   */
  async directories(
    pagination: PaginationOptions = {
      cursor: null,
      limit: null
    }
  ) {
    const response = await this.requestWithStatus<ResGetEnterpriseDirectories>(
      "GET",
      this.paginatedEndpointUrl("/v1/directory/directories", pagination)
    );
    return response;
  }

  /**
   * Creates a new pending directory for the organization.
   *
   * Restricted to Tactic employees by the backend: every live directory has a
   * cost attached to it.
   */
  async createDirectory(data: CreateDirectoryRequest) {
    return await this.requestWithStatus<Directory>(
      "POST",
      "/v1/directories",
      data
    );
  }

  /**
   * Updates the name and/or the synced domains of an existing directory.
   *
   * Only the fields present in the request are changed. Domains must be a
   * subset of the organization's authorized domains.
   */
  async updateDirectory(id: string, data: UpdateDirectoryRequest) {
    return await this.requestWithStatus<Directory>(
      "PUT",
      `/v1/directories/${id}`,
      data
    );
  }

  /**
   * Creates a new SSO connection for the organization.
   */
  async createConnection(data: CreateConnectionRequest) {
    return await this.requestWithStatus<Connection>(
      "POST",
      "/v1/connections",
      data
    );
  }

  /**
   * Updates an existing SSO connection.
   */
  async updateConnection(id: string, data: UpdateConnectionRequest) {
    return await this.requestWithStatus<Connection>(
      "PUT",
      `/v1/connections/${id}`,
      data
    );
  }

  /**
   * Fetches a single SSO connection by ID.
   */
  async getConnection(id: string) {
    return await this.requestWithStatus<Connection>(
      "GET",
      `/v1/connections/${id}`
    );
  }

  /**
   * Deletes an SSO connection by ID.
   */
  async deleteConnection(id: string) {
    return await this.requestWithStatus<unknown>(
      "DELETE",
      `/v1/connections/${id}`
    );
  }

  /**
   * Lists SSO login sessions for a connection.
   */
  async listSessions(
    connectionId: string,
    pagination: PaginationOptions = {
      cursor: null,
      limit: "200"
    }
  ) {
    return await this.requestWithStatus<ResGetConnectionSessions>(
      "GET",
      this.paginatedEndpointUrl(
        `/v1/connections/${connectionId}/sessions`,
        pagination
      )
    );
  }
}
